import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form2290MobileApp = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="E-file Form 2290 Easily with TaxZerone Mobile App"
          description="Download the TaxZerone Mobile App to e-file Form 2290 anytime, anywhere. Get your IRS Schedule 1 instantly with fast, secure, and hassle-free filing"
          keywords=""
          canonical="resources/excise-tax/filing-form-2290-through-the-mobile-app/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          Filing Form 2290 using the{' '}
          <span className="highlight-container">
            <span className="highlight"> Mobile App</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File your Form 2290 anywhere, anytime with TaxZerone Mobile App!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=mobileapp`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Box
                      itemScope
                      itemType="https://schema.org/Article"
                      sx={{
                        p: 3,
                        px: 3,
                        backgroundColor: '#f0f5f8',
                        borderLeft: '5px solid #01456c',
                        mb: 4,
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <Link
                        itemProp="mainEntityOfPage"
                        href="https://www.taxzerone.com/resources/excise-tax/filing-form-2290-through-the-mobile-app/"
                        style={{ display: 'none' }}
                      />

                      {/* Article requires an image */}
                      <div
                        itemProp="image"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                        style={{ display: 'none' }}
                      >
                        <Link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>

                      <Box
                        sx={{
                          display: 'grid',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          textAlign: 'left',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          Last updated:{' '}
                          <time
                            itemProp="datePublished"
                            dateTime="2026-08-21T09:00:00-05:00"
                          >
                            August 21, 2026
                          </time>
                          <time
                            itemProp="dateModified"
                            dateTime="2026-08-21T09:00:00-05:00"
                            style={{ display: 'none' }}
                          >
                            August 21, 2026
                          </time>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          -by{' '}
                          <span itemProp="name">
                            <Link
                              itemProp="url"
                              href="https://www.taxzerone.com/"
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              Alexia Zepeda
                            </Link>
                          </span>{' '}
                          | TaxZerone
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        itemProp="headline"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: { md: 20, sm: 18, xs: 16 },
                          color: '#01456c',
                          fontStyle: 'italic',
                        }}
                      >
                        Form 2290 Mobile App Filing at a Glance
                      </Typography>

                      <Box
                        component="ul"
                        itemScope
                        itemType="https://schema.org/ItemList"
                        sx={{
                          mb: 0,
                          pl: 0,
                          listStyle: 'none',

                          '& li': {
                            mb: 1.6,
                            position: 'relative',
                            pl: 3,
                          },

                          '& li::before': {
                            content: '"★"',
                            position: 'absolute',
                            left: 0,
                            top: '-2px',
                            color: '#01456c',
                            fontSize: '18px',
                          },
                        }}
                      >
                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="1"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Who Can Use It:
                            </strong>{' '}
                            Truckers and fleet owners can file Form 2290{' '}
                            <strong style={{ fontWeight: 600 }}>
                              anytime, anywhere
                            </strong>{' '}
                            using a mobile app.
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="2"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Benefits:
                            </strong>{' '}
                            Mobile e-filing offers{' '}
                            <strong style={{ fontWeight: 600 }}>
                              faster submission, fewer errors, real-time
                              tracking, and instant access to Schedule 1.
                            </strong>
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="3"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Requirements:
                            </strong>{' '}
                            Keep your{' '}
                            <strong style={{ fontWeight: 600 }}>
                              EIN, business details, VIN, taxable gross weight,
                              and vehicle information
                            </strong>{' '}
                            ready before filing.
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="4"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Filing Process:
                            </strong>{' '}
                            Download the app, enter vehicle details, review tax
                            calculations, choose a payment method, and submit
                            your return to the IRS.
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="5"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Payment Options:
                            </strong>{' '}
                            Pay HVUT using{' '}
                            <Link
                              href="/resources/eftps/"
                              style={{ color: '#0202ff' }}
                            >
                              EFTPS
                            </Link>
                            ,{' '}
                            <strong style={{ fontWeight: 600 }}>
                              Electronic Funds Withdrawal{' '}
                            </strong>
                            <Link
                              href="/resources/electronic-funds-withdrawal-efw/"
                              style={{ color: '#0202ff' }}
                            >
                              EFW
                            </Link>
                            , or{' '}
                            <strong style={{ fontWeight: 600 }}>
                              credit/debit card.
                            </strong>
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="6"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Avoid Errors:
                            </strong>{' '}
                            Double-check{' '}
                            <strong style={{ fontWeight: 600 }}>
                              VIN, EIN, weight category, and first-used month
                            </strong>{' '}
                            to prevent filing delays or rejections.
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="7"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>Result:</strong>{' '}
                            Receive your{' '}
                            <strong style={{ fontWeight: 600 }}>
                              IRS-stamped Schedule 1 electronically
                            </strong>{' '}
                            after successful IRS acceptance.
                          </span>
                        </li>
                      </Box>

                      <div
                        itemProp="publisher"
                        itemScope
                        itemType="https://schema.org/Organization"
                        style={{ display: 'none' }}
                      >
                        <span itemProp="name">TaxZerone</span>

                        <div
                          itemProp="logo"
                          itemScope
                          itemType="https://schema.org/ImageObject"
                        >
                          <Link
                            itemProp="url"
                            href="https://www.taxzerone.com/assets/images/logo.webp"
                          />
                          <data itemProp="width" value="380" />
                          <data itemProp="height" value="124" />
                        </div>
                      </div>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Tired of dealing with paper forms or sitting at a computer
                      to file your IRS Form 2290? With the TaxZerone Mobile App,
                      truckers and fleet owners can now e-file their{' '}
                      <Link
                        href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Heavy Vehicle Use Tax
                      </Link>{' '}
                      (HVUT) form right from their phone—anytime, anywhere.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                      This page will take you through how to file Form 2290
                      using a mobile app, why it’s a smarter way to file, and
                      what to keep in mind when choosing the best e-filing app
                      for life on the road.
                    </Typography>
                  </Box>
                  <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  />
                </Stack>

                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why file Form 2290 using a Mobile App?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#benefits" sx={{ color: '#0202ff' }}>
                          Benefits of Electronic Filing Over Paper Filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#step" sx={{ color: '#0202ff' }}>
                          Step-by-step: Filling Form 2290 via Mobile App
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common Mistakes to Avoid When Filing Form 2290 via
                          Mobile
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="why"
                >
                  Why file Form 2290 using a Mobile App?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Filing Form 2290 through a{' '}
                  <Link href="/mobile-apps/" sx={{ color: '#0202ff' }}>
                    {' '}
                    mobile app
                  </Link>{' '}
                  provides convenience, speed, and flexibility, especially for
                  truckers and business owners who are often on the road. Mobile
                  apps allow you to complete and submit your tax return from
                  anywhere, at any time, and receive immediate proof of filing.
                </Typography>

                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Need to File Form 2290 for Your Heavy Vehicle?
                      </Typography>
                      <Typography variant="body2">
                        Start filing with the TaxZerone Mobile App today and
                        complete your HVUT Form 2290 and get your Stamped
                        schedule 1 instantly!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 250, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=mobileapp`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="benefits"
                >
                  Benefits of Electronic Filing Over Paper Filing
                </Typography>

                <Typography variant="body1">
                  Paper filing is slow, prone to errors, and can delay your 2290
                  Schedule 1. E-filing through TaxZerone is:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}> Faster:</span> Get your
                    <Link
                      href="/resources/excise-tax/form-2290-schedule-1/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      IRS-stamped Schedule 1
                    </Link>{' '}
                    within minutes.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      More Accurate:
                    </span>{' '}
                    Smart error checks reduce rejections.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      More Convenient:
                    </span>{' '}
                    File 24/7 without visiting an IRS office.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}> Trackable:</span> Know
                    the status of your return in real time.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}> Eco-Friendly:</span> No
                    printing, no mailing, no paper waste.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="step"
                >
                  Step-by-step: Filling Form 2290 via Mobile App
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Using the{' '}
                  <Link href="/mobile-apps/" sx={{ color: '#0202ff' }}>
                    {' '}
                    TaxZerone Mobile App
                  </Link>{' '}
                  is quick and easy. Simply follow the steps below to complete
                  your filing.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 1: Download and Install the TaxZerone App{' '}
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Download the app from the{' '}
                  <Link
                    href="https://apps.apple.com/us/app/e-file-form-2290-pay-hvut/id6450998674"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Apple App Store
                  </Link>{' '}
                  (iOS) or{' '}
                  <Link
                    href="https://play.google.com/store/apps/details?id=com.taxzerone.excisetaxes"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    Google Play Store
                  </Link>{' '}
                  (Android).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 2: Create or log in to Your Account
                </Typography>
                <picture>
                  <source srcSet="/assets/images/exicse-app-home-img.webp" />
                  <source srcSet="/assets/images/exicse-app-home-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/exicse-app-home-img.png"
                    loading="lazy"
                    alt="Form 2290 Mobile App - Step 2: Create or Sign in to your account "
                    width={190}
                    height={401}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Register with your business information, including your
                  Employer Identification Number (EIN). Make sure you have an
                  Employer Identification Number (EIN).{' '}
                </Typography>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesIcon />}
                  sx={{ mb: 4 }}
                >
                  <b>Note:</b> The IRS does not accept a Social Security Number
                  (SSN) for Form 2290 filing.
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 3: Enter your business details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/exicse-app-business-img.webp" />
                  <source srcSet="/assets/images/exicse-app-business-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/exicse-app-business-img.png"
                    loading="lazy"
                    alt="Form 2290 Mobile App - Step 3: Enter your business information such as Business type, name, EIN and address as per the SS-4 copy issued by the IRS."
                    width={190}
                    height={401}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Business details including name, EIN, address, and
                  contact information. Ensure all information is accurate and
                  matches official records.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 4: Enter the Vehicle Details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/exicse-app-vehicle-img.webp" />
                  <source srcSet="/assets/images/exicse-app-vehicle-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/exicse-app-vehicle-img.png"
                    loading="lazy"
                    alt="Form 2290 Mobile App: Add your taxable vehicle details such as VIN, Taxable gross weight (in pounds) and select the logging vehicle box (if applicable)."
                    width={190}
                    height={401}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Provide vehicle information such as Vehicle Identification
                    Number (VIN),{' '}
                    <Link
                      href="/resources/excise-tax/form-2290-taxable-gross-weight-and-weight-categories/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      taxable gross weight
                    </Link>
                    .
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Enter the Suspended Vehicle
                    </span>{' '}
                    <br />
                    If your vehicle qualifies as suspended (driven less than
                    5,000 miles, or 7,500 for agricultural use), enter its
                    details here. Or otherwise, you can{' '}
                    <span style={{ fontWeight: 600 }}>skip</span> this step.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/exicse-app-suspended-img.webp" />
                  <source srcSet="/assets/images/exicse-app-suspended-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/exicse-app-suspended-img.png"
                    loading="lazy"
                    alt="Form 2290 Mobile App: Add your suspended vehicle VIN. Select the logging vehicle or agricultural vehicle check box (if applicable)."
                    width={190}
                    height={401}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Enter the Prior year suspended vehicle
                    </span>{' '}
                    <br />
                    If your vehicle was reported as suspended on last year’s
                    Form 2290, enter its details here. If not, you can{' '}
                    <span style={{ fontWeight: 600 }}>skip</span> this step.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/exicse-app-prior-img.webp" />
                  <source srcSet="/assets/images/exicse-app-prior-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/exicse-app-prior-img.png"
                    loading="lazy"
                    alt="Form 2290 Mobile App: Add your Prior Year Suspended Vehicle VIN and select the reason (mileage exceeded or sold /transferred) for reporting this vehicle."
                    width={190}
                    height={401}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 4,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 5: Calculate and Review Your Tax
                </Typography>
                <picture>
                  <source srcSet="/assets/images/exicse-app-cal-review-img.webp" />
                  <source srcSet="/assets/images/exicse-app-cal-review-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/exicse-app-cal-review-img.png"
                    loading="lazy"
                    alt="Form 2290 Mobile App automatically calculates the amount of HVUT you owe based on your vehicle’s weight."
                    width={190}
                    height={401}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The app will automatically{' '}
                  <Link
                    href="/tools/form-2290-tax-calculator/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    calculate the amount of HVUT
                  </Link>{' '}
                  you owe based on your vehicle’s weight.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 6: Choose Payment Method
                </Typography>
                <picture>
                  <source srcSet="/assets/images/exicse-app-payment-img.webp" />
                  <source srcSet="/assets/images/exicse-app-payment-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/exicse-app-payment-img.png"
                    loading="lazy"
                    alt="Form 2290 Mobile App: Select a payment option such as EFTPS, EFW, or credit/debit card."
                    width={190}
                    height={401}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select a payment option such as{' '}
                  <Link href="/resources/eftps/" sx={{ color: '#0202ff' }}>
                    {' '}
                    EFTPS
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/electronic-funds-withdrawal-efw/"
                    sx={{ color: '#0202ff' }}
                  >
                    EFW
                  </Link>
                  , or credit/debit card.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 7: Submit Your Form 2290
                </Typography>
                <picture>
                  <source srcSet="/assets/images/exicse-app-sumbit-img.webp" />
                  <source srcSet="/assets/images/exicse-app-sumbit-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/exicse-app-sumbit-img.png"
                    loading="lazy"
                    alt="Form 2290 Mobile App: Review your information and submit the form directly to the IRS through the app."
                    width={190}
                    height={401}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Review your information and submit the form directly to the
                  IRS through the app.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #ccc',
                    backgroundColor: '#e3f2fd',
                  }}
                  // className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body2">
                        <span style={{ fontWeight: 600 }}>
                          ðŸ‘‰Need help to file Form 2290?
                        </span>{' '}
                        Refer to the{' '}
                        <Link
                          href="/resources/excise-tax/form-2290-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Form 2290 instructions
                        </Link>{' '}
                        for step-by-step guidance on completing and submitting
                        your return.
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="common"
                >
                  Common Mistakes to Avoid When Filing Form 2290 via Mobile
                </Typography>

                <Typography variant="body1">
                  While mobile filing is fast and convenient, here are some
                  common errors to watch out for:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 2,
                    ml: -2.5,
                    listStyleType: 'none',
                    '& li': { mb: 1.5 },
                  }}
                >
                  <li>
                    {' '}
                    ❌{' '}
                    <span style={{ fontWeight: 600 }}>
                      Entering the wrong VIN –
                    </span>{' '}
                    One incorrect character can lead to rejections.
                  </li>
                  <li>
                    ❌{' '}
                    <span style={{ fontWeight: 600 }}>
                      Choosing the wrong weight category –
                    </span>{' '}
                    This affects your tax amount.
                  </li>
                  <li>
                    {' '}
                    ❌{' '}
                    <span style={{ fontWeight: 600 }}>
                      Missing the first-used month –
                    </span>{' '}
                    Filing late for that month leads to penalties.
                  </li>
                  <li>
                    {' '}
                    ❌{' '}
                    <span style={{ fontWeight: 600 }}>
                      Not submitting for suspended vehicles –
                    </span>{' '}
                    You must still file even if no tax is due.
                  </li>
                  <li>
                    {' '}
                    ❌{' '}
                    <span style={{ fontWeight: 600 }}>
                      Using an incorrect EIN –
                    </span>{' '}
                    Your EIN must be valid and at least 14 days old.
                  </li>
                </Box>
                <Alert
                  severity="info"
                  id="happen"
                  icon={false}
                  sx={{ mb: 3, mt: 0 }}
                >
                  <b>Tip:</b> Double-check every field before submitting to
                  avoid IRS rejections or delays.
                </Alert>

                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #ccc',
                    backgroundColor: '#e3f2fd',
                  }}
                  // className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Important:
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        If you need to file a{' '}
                        <Link
                          href="/excise-tax/efile-irs-form-2290-vin-correction-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          VIN correction
                        </Link>{' '}
                        or an{' '}
                        <Link
                          href="/resources/excise-tax/form-2290-amendments/"
                          sx={{ color: '#0202ff' }}
                        >
                          amended return
                        </Link>
                        , such as updating the vehicle’s weight or reporting
                        mileage over the suspension limit, please visit
                        <Link href="/excise-tax/" sx={{ color: '#0202ff' }}>
                          {' '}
                          TaxZerone website
                        </Link>{' '}
                        for further instructions and easy filings.
                      </Typography>
                      <Typography variant="body2">
                        {' '}
                        ðŸŽ¥ For help, watch our step-by-step videos here: [
                        <Link
                          href="https://www.youtube.com/playlist?list=PLmvRKAW8mnOiHgODlf35zeTg7wBnQ1SZg"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          Watch Tutorial Videos
                        </Link>
                        ]
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to File Your Form 2290 the Easy Way?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Download the TaxZerone Mobile App and file your Form 2290 in just
              a few taps—fast, secure,
              <br /> and IRS-authorized and get your stamped Schedule 1
              instantly.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href="/mobile-apps/"
              >
                Download Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form2290MobileApp;
